import { useCallback, useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQueryClient } from 'react-query';
import { FreeTrialSubscriptionQueries, FreeTrialSubscriptionTypes } from '@frontend/api-free-trial-subscription';
import {
  SubscriptionManagerServiceName,
  useIsMultiEligibleForSelfUpgradeOrTrialQuery,
  type ServiceQueries as SubscriptionManagerServiceQueries,
} from '@frontend/api-subscription-manager';
import { checkAggregateACL } from '@frontend/auth-helpers';
import { SchemaQueryKey } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { FeatureSubscriptionContextProps } from '../providers/FeatureSubscriptionProvider';
import {
  buildLocationSubscriptionData,
  checkSelfSubscribeEligibility,
  checkSubscriptionActive,
  checkSubscriptionActiveWithoutTrial,
  checkTrialEligibility,
  checkUpgradeEligibility,
} from '../self-upgrade.util';
import { useBundleUpgradeEligibility } from './useBundleUpgradeEligibility';

interface FeatureSubscriptionConfig extends FeatureSubscriptionContextProps {
  isFeatureActiveInAllLocations: boolean;
  isFeatureHiddenInAllLocations: boolean;
  isFeatureActiveInSomeLocations: boolean;
  isFeatureActiveWithoutTrial: boolean;
  isLoading: boolean;
  minimumTrialEndDate: Date | null;
}

const DEFAULT_ACTIVE_STATES = [State.ACTIVE];
const DEFAULT_CACHE_TIME = 12 * 60 * 60 * 1000; // 12 hours

export const useFeatureSubscriptionConfig = ({
  feature,
  locationIds,
  checkBundleUpgrade = true,
  isTrialAllowed = true,
  isSelfSubscribeAllowed = true,
  activeStates = DEFAULT_ACTIVE_STATES,
}: {
  feature: Feature;
  locationIds?: string[];
  checkBundleUpgrade?: boolean;
  isTrialAllowed?: boolean;
  isSelfSubscribeAllowed?: boolean;
  activeStates?: State[];
}): FeatureSubscriptionConfig => {
  const { selectedLocationIds: appScopeLocationIds } = useAppScopeStore();
  const queryClient = useQueryClient();

  const selectedLocationIds = locationIds || appScopeLocationIds;
  const { getCustomizationFlagLocationMap } = useAppFlagStore();

  const locationWiseCFMap = getCustomizationFlagLocationMap({
    locationIds: selectedLocationIds,
    featureEnum: feature,
  });

  const { aclValuesByLocationId: locationWiseAdminAccess, aggregateValue: hasAdminAccessInAtLeastOne } = useMemo(() => {
    return checkAggregateACL(selectedLocationIds, Permission.MANAGE_ADD_ON_SUBSCRIPTIONS, 'any');
  }, [selectedLocationIds]);

  const { data: subscriptionEligibilityData, isLoading: isLoadingSelfUpgradeData } =
    useIsMultiEligibleForSelfUpgradeOrTrialQuery({
      request: {
        featureEnum: feature,
        locationIds: selectedLocationIds.filter(Boolean),
      },
      options: {
        cacheTime: DEFAULT_CACHE_TIME,
        staleTime: DEFAULT_CACHE_TIME,
        enabled: !!(selectedLocationIds?.filter(Boolean).length && feature),
      },
    });

  const locationWiseSubscriptionEligibilityMap = useMemo(() => {
    return (subscriptionEligibilityData?.eligibility || [])
      .filter(({ error }) => !error)
      .reduce((acc, eligibilityData) => {
        acc[eligibilityData.locationId] = eligibilityData;
        return acc;
      }, {} as Record<string, FreeTrialSubscriptionTypes.IsMultiEligibleForSelfUpgradeOrTrialDetail>);
  }, [subscriptionEligibilityData]);

  const {
    subscriptionDataList,
    isSelfSubscribeEligible,
    isUpgradeEligible,
    isFreeTrialEligible,
    isUpgradeOrTrialEligible,
    isFeatureHiddenInAllLocations,
    isFeatureActiveInAllLocations,
    isFeatureActiveInSomeLocations,
    isFeatureActiveWithoutTrial,
    minimumTrialEndDate,
  } = useMemo(() => {
    const subscriptionDataList = buildLocationSubscriptionData({
      selectedLocationIds,
      locationWiseCFMap,
      locationWiseSubscriptionEligibilityMap,
      locationWiseAdminAccess,
      isSelfSubscribeAllowed,
      isTrialAllowed,
      activeStates,
    });
    const isUpgradeEligible = subscriptionDataList.some(checkUpgradeEligibility);
    const isSelfSubscribeEligible = subscriptionDataList.some(checkSelfSubscribeEligibility);
    const isFreeTrialEligible = subscriptionDataList.some(checkTrialEligibility);

    const isFeatureActiveInSomeLocations =
      !!subscriptionDataList.length && subscriptionDataList.some(checkSubscriptionActive);
    const isFeatureActiveInAllLocations =
      !!subscriptionDataList.length && subscriptionDataList.every(checkSubscriptionActive);
    const isFeatureActiveWithoutTrial =
      !!subscriptionDataList.length && subscriptionDataList.every(checkSubscriptionActiveWithoutTrial);
    const isFeatureHiddenInAllLocations =
      !!subscriptionDataList.length &&
      subscriptionDataList.every((subscriptionData) => !checkSubscriptionActive(subscriptionData));

    const minimumTrialEndDate = subscriptionDataList
      .filter((data) => data.trialEndDate)
      .reduce<Date | null>((minDate, data) => {
        const trialEndDate = data.trialEndDate ? new Date(data.trialEndDate) : null;
        if (!trialEndDate) {
          return minDate;
        }
        if (!minDate || trialEndDate < minDate) {
          return trialEndDate;
        }
        return minDate;
      }, null);

    return {
      subscriptionDataList,
      isUpgradeEligible,
      isFeatureHiddenInAllLocations,
      isFeatureActiveInSomeLocations,
      isFeatureActiveInAllLocations,
      isFeatureActiveWithoutTrial,
      isFreeTrialEligible,
      isUpgradeOrTrialEligible: isUpgradeEligible || isFreeTrialEligible,
      isSelfSubscribeEligible,
      minimumTrialEndDate,
    };
  }, [
    selectedLocationIds,
    locationWiseCFMap,
    locationWiseSubscriptionEligibilityMap,
    locationWiseAdminAccess,
    isSelfSubscribeAllowed,
    isTrialAllowed,
    activeStates,
  ]);

  const { isBundleUpgradeEligible, recommendedBundle, isLoadingBundleUpgradeEligibility } = useBundleUpgradeEligibility(
    {
      locationIds: selectedLocationIds,
      enabled: checkBundleUpgrade && isUpgradeOrTrialEligible,
    }
  );

  const invalidateSubscriptionConfig = useCallback(() => {
    // Invalidate all queries related to subscription config and platform location features (customization flags)
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) => {
        if (
          Array.isArray(queryKey) &&
          queryKey[0] === SubscriptionManagerServiceName &&
          queryKey[1] === 'IsMultiEligibleForSelfUpgradeOrTrial'
        ) {
          const trialEligibilityQueryKey = queryKey as SchemaQueryKey<
            SubscriptionManagerServiceQueries,
            'IsMultiEligibleForSelfUpgradeOrTrial'
          >;
          return !!(
            trialEligibilityQueryKey[2].featureEnum === feature &&
            trialEligibilityQueryKey[2].locationIds?.some((id) => selectedLocationIds?.includes(id))
          );
        }

        if (
          Array.isArray(queryKey) &&
          queryKey[0] === SubscriptionManagerServiceName &&
          queryKey[1] === 'GetSubscriptionAddOnsStatusesForMulti'
        ) {
          const subscriptionAddonListQueryKey = queryKey as SchemaQueryKey<
            SubscriptionManagerServiceQueries,
            'GetSubscriptionAddOnsStatusesForMulti'
          >;

          return !!subscriptionAddonListQueryKey[2].locationIds?.some((id) => selectedLocationIds?.includes(id));
        }

        return (
          queryKey.includes('customization-flags') ||
          queryKey.includes('customization') ||
          ((selectedLocationIds ?? []).some((locationId) => queryKey.includes(locationId)) &&
            FreeTrialSubscriptionQueries.freeTrialSubscriptionQueryKeys.subscriptionBaseQuery.every((part) =>
              queryKey.includes(part)
            ))
        );
      },
    });
  }, [feature, queryClient, selectedLocationIds]);

  return {
    selectedLocationIds,
    feature,
    isUpgradeOrTrialEligible,
    isFreeTrialEligible,
    isUpgradeEligible,
    isSelfSubscribeEligible,
    isFeatureHiddenInAllLocations,
    isFeatureActiveInAllLocations,
    isFeatureActiveInSomeLocations,
    isFeatureActiveWithoutTrial,
    isLoading: isLoadingSelfUpgradeData || isLoadingBundleUpgradeEligibility,
    invalidateSubscriptionConfig,
    subscriptionDataList,
    locationWiseAdminAccess,
    hasAdminAccessInAtLeastOne,
    isBundleUpgradeEligible,
    recommendedBundle,
    minimumTrialEndDate,
  };
};
